import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Users } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { ROUTES } from '@/routes/paths';
import { ShiftEditor } from '../components/ShiftEditor';
import { RosterEditor } from '../components/RosterEditor';

type Tab = 'shifts' | 'rosters';

/**
 * Shifts & Rosters Master Data page — standalone route for managing shift
 * definitions and employee-to-shift roster assignments. Accessible from the
 * System > Master Data hub.
 */
export function ShiftMasterDataPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('shifts');

  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="flex items-center justify-between border-b border-outline px-6 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTES.masterData)}
          aria-label="Back to Master Data"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Master Data
        </Button>
      </div>

      {/* Tab switcher */}
      <div className="px-6 pt-4">
        <div className="flex gap-1 rounded bg-surface-variant/50 p-1 w-fit">
          <button
            type="button"
            onClick={() => setTab('shifts')}
            className={`flex items-center gap-1.5 rounded px-4 py-1.5 text-body-sm font-medium transition-colors ${
              tab === 'shifts'
                ? 'bg-surface text-on-surface shadow-surface-1'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Clock className="h-3.5 w-3.5" />
            Shift Definitions
          </button>
          <button
            type="button"
            onClick={() => setTab('rosters')}
            className={`flex items-center gap-1.5 rounded px-4 py-1.5 text-body-sm font-medium transition-colors ${
              tab === 'rosters'
                ? 'bg-surface text-on-surface shadow-surface-1'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Users className="h-3.5 w-3.5" />
            Rosters
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6">
        {tab === 'shifts' ? <ShiftEditor /> : <RosterEditor />}
      </div>
    </div>
  );
}
